import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Customer Store
 * Manages single customer and list of customers
 */
export const useCustomer = create((set) => ({
  // State
  customer: null,
  customers: [],

  // Actions
  setCustomer: (customer) => set({ customer }),
  clearCustomer: () => set({ customer: null }),
  setCustomers: (customers) => set({ customers }),
  addCustomer: (customer) =>
    set((state) => ({
      customers: [...state.customers, customer],
    })),
  updateCustomer: (customerId, updatedData) =>
    set((state) => ({
      customers: state.customers.map((c) =>
        c.id === customerId ? { ...c, ...updatedData } : c
      ),
    })),
  removeCustomer: (customerId) =>
    set((state) => ({
      customers: state.customers.filter((c) => c.id !== customerId),
    })),
  clearCustomers: () => set({ customers: [] }),
  reset: () => set({ customer: null, customers: [] }),
}));

/**
 * Bookings Store
 * Manages bookings data
 */
export const useBookings = create((set) => ({
  bookings: [],
  setBookings: (bookings) => set({ bookings }),
  addBooking: (booking) =>
    set((state) => ({
      bookings: [...state.bookings, booking],
    })),
  updateBooking: (bookingId, updatedData) =>
    set((state) => ({
      bookings: state.bookings.map((b) =>
        b.id === bookingId 
          ? { ...b, ...updatedData } 
          : b
      ),
    })),
  removeBooking: (bookingId) =>
    set((state) => ({
      bookings: state.bookings.filter((b) => 
        b.id !== bookingId
      ),
    })),
  clearBookings: () => set({ bookings: [] }),
}));

/**
 * Completed Bookings Store
 * Caches completed bookings (fetched on-demand)
 */
/**
 * Bookings that exist but are not yet paid/confirmed. Used to place SOFT HOLDS on
 * inventory during checkout so two customers cannot both be sold the last item.
 *
 * There is deliberately no "release" action. A hold is a derived fact — "a pending
 * booking younger than HOLD_WINDOW_MS" — so an abandoned or crashed session ages out
 * on its own. Modelling it as a lock with an explicit unlock is what would create a
 * stuck-forever state, because any release step can fail.
 */
export const usePendingBookings = create((set) => ({
  pendingBookings: [],
  setPendingBookings: (pendingBookings) => set({ pendingBookings }),
  clearPendingBookings: () => set({ pendingBookings: [] }),
}));

export const useCompletedBookings = create((set) => ({
  completedBookings: [],
  completedLoaded: false,
  setCompletedBookings: (bookings) => set({ completedBookings: bookings, completedLoaded: true }),
  clearCompletedBookings: () => set({ completedBookings: [], completedLoaded: false }),
}));

/**
 * Locks Store
 * Manages locks data
 */
export const useLocks = create((set) => ({
  locks: [],
  setLocks: (locks) => set({ locks }),
  addLock: (lock) =>
    set((state) => ({
      locks: [...state.locks, lock],
    })),
  updateLock: (lockId, updatedData) =>
    set((state) => ({
      locks: state.locks.map((l) =>
        l.id === lockId ? { ...l, ...updatedData } : l
      ),
    })),
  removeLock: (lockId) =>
    set((state) => ({
      locks: state.locks.filter((l) => l.id !== lockId),
    })),
  clearLocks: () => set({ locks: [] }),
}));

/**
 * Products Store
 * Manages products1 and products2 data
 */
export const useProducts = create((set) => ({
  products1: [],
  products2: [],
  setProducts1: (products) => set({ products1: products }),
  setProducts2: (products) => set({ products2: products }),
  clearProducts: () => set({ products1: [], products2: [] }),
}));

/**
 * Settings Store
 * Manages settings cache
 */
export const useSettings = create((set) => ({
  settings: {},
  lastUpdated: null,
  setSettings: (settings) => set({ 
    settings, 
    lastUpdated: new Date().toISOString() 
  }),
  updateSetting: (key, value) =>
    set((state) => ({
      settings: { ...state.settings, [key]: value },
      lastUpdated: new Date().toISOString(),
    })),
  clearSettings: () => set({ settings: {}, lastUpdated: null }),
}));

/**
 * Stripe Test Mode — browser-local only (localStorage). Not saved to settings/Firestore.
 */
export const useStripeTestMode = create(
  persist(
    (set) => ({
      useTestStripe: false,
      setUseTestStripe: (value) => set({ useTestStripe: !!value }),
    }),
    { name: 'pix-stripe-test-mode' }
  )
);

/**
 * Firestore Cache Status Store
 * Tracks when Firestore is serving cached data
 */
export const useFirestoreCacheStatus = create((set) => ({
  isServingCache: false,
  setServingCache: (isServing) => set({ isServingCache: isServing }),
  clearCacheStatus: () => set({ isServingCache: false }),
}));

/**
 * User Store
 * Manages user-specific store settings from /email_users/{uid}
 */
export const useUserStore = create((set) => ({
  userStore: null,
  setUserStore: (data) => set({ userStore: data }),
  clearUserStore: () => set({ userStore: null }),
}));

// Gmail messages store with localStorage persistence
export const useGmailMessages = create(
  persist(
    (set, get) => ({
      messages: [],
      lastUpdated: null,
      setMessages: (messages) => set({ 
        messages, 
        lastUpdated: new Date().toISOString() 
      }),
      addMessage: (message) => {
        const currentMessages = get().messages;
        const exists = currentMessages.find(m => m.id === message.id);
        if (!exists) {
          set({ 
            messages: [message, ...currentMessages], 
            lastUpdated: new Date().toISOString() 
          });
        }
      },
      updateMessage: (id, updates) => {
        const currentMessages = get().messages;
        const updatedMessages = currentMessages.map(msg =>
          msg.id === id ? { ...msg, ...updates } : msg
        );
        set({ 
          messages: updatedMessages, 
          lastUpdated: new Date().toISOString() 
        });
      },
      removeMessage: (id) => {
        const currentMessages = get().messages;
        set({ 
          messages: currentMessages.filter(msg => msg.id !== id),
          lastUpdated: new Date().toISOString()
        });
      },
      clearMessages: () => set({ messages: [], lastUpdated: null }),
    }),
    {
      name: 'gmail-messages-storage',
    }
  )
);

/**
 * Gmail Folders Store
 * Manages Gmail labels/folders data synced from backend
 */
export const useGmailFolders = create(
  persist(
    (set) => ({
      folders: [],
      lastUpdated: null,
      setFolders: (folders) => set({ 
        folders, 
        lastUpdated: new Date().toISOString() 
      }),
      updateFolder: (folderId, updates) =>
        set((state) => ({
          folders: state.folders.map((f) =>
            f.id === folderId ? { ...f, ...updates } : f
          ),
          lastUpdated: new Date().toISOString(),
        })),
      clearFolders: () => set({ folders: [], lastUpdated: null }),
    }),
    {
      name: 'gmail-folders-storage',
    }
  )
);

/**
 * Selected Date Store
 * Manages selected dates for Calendar and Base pages
 */
export const useSelectedDate = create(
  persist(
    (set, get) => ({
      calendarDate: null, // Will be initialized to today
      baseDate: null, // Will be initialized to today
      calendarDateSelectedAt: null,
      baseDateSelectedAt: null,
      todayProductsListVisible: true,
      todayProductsListSelectedAt: null,
      
      setCalendarDate: (date) => {
        set({
          calendarDate: date ? date.getTime() : null,
          calendarDateSelectedAt: date ? Date.now() : null,
        });
      },
      
      getCalendarDate: () => {
        const timestamp = get().calendarDate;
        if (!timestamp) {
          // Initialize with today
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          set({
            calendarDate: today.getTime(),
            calendarDateSelectedAt: Date.now(),
          });
          return today;
        }
        const date = new Date(timestamp);
        date.setHours(0, 0, 0, 0);
        return date;
      },
      getCalendarDateTimestamp: () => get().calendarDateSelectedAt,
      
      setBaseDate: (date) => {
        set({
          baseDate: date ? date.getTime() : null,
          baseDateSelectedAt: date ? Date.now() : null,
        });
      },
      
      getBaseDate: () => {
        const timestamp = get().baseDate;
        if (!timestamp) {
          // Initialize with today - use local timezone to avoid offset issues
          const today = new Date();
          // Set to midnight in local timezone to avoid timezone conversion issues
          today.setHours(0, 0, 0, 0);
          const todayTime = today.getTime();
          set({
            baseDate: todayTime,
            baseDateSelectedAt: Date.now(),
          });
          // Return a new Date object to avoid reference issues
          const returnDate = new Date(todayTime);
          returnDate.setHours(0, 0, 0, 0);
          return returnDate;
        }
        const date = new Date(timestamp);
        // Ensure hours are set to 0 to avoid timezone offset issues
        date.setHours(0, 0, 0, 0);
        return date;
      },
      getBaseDateTimestamp: () => get().baseDateSelectedAt,

      setTodayProductsListVisible: (visible) => {
        set({
          todayProductsListVisible: visible !== false,
          todayProductsListSelectedAt: Date.now(),
        });
      },

      getTodayProductsListVisible: () => {
        const visible = get().todayProductsListVisible;
        if (typeof visible !== "boolean") {
          set({
            todayProductsListVisible: true,
            todayProductsListSelectedAt: Date.now(),
          });
          return true;
        }
        return visible;
      },

      getTodayProductsListTimestamp: () => get().todayProductsListSelectedAt,
    }),
    {
      name: 'selected-date-storage',
    }
  )
);

/**
 * Route Store
 * Manages cached routes by day key
 * Structure: { [dayKey]: { route, bookingsHash, timestamp } }
 */
export const useRoute = create(
  persist(
    (set, get) => ({
      routes: {}, // { [dayKey]: { route, bookingsHash, timestamp } }
      
      // Get route for a specific day
      getRoute: (dayKey) => {
        const routes = get().routes;
        return routes[dayKey] || null;
      },
      
      // Set route for a specific day with bookings hash
      setRoute: (dayKey, route, bookingsHash) => {
        set((state) => ({
          routes: {
            ...state.routes,
            [dayKey]: {
              route,
              bookingsHash,
              timestamp: new Date().toISOString(),
            },
          },
        }));
      },
      
      // Check if route is valid (bookings haven't changed)
      isRouteValid: (dayKey, currentBookingsHash) => {
        const cached = get().routes[dayKey];
        if (!cached) return false;
        return cached.bookingsHash === currentBookingsHash;
      },
      
      // Clear route for a specific day
      clearRoute: (dayKey) => {
        set((state) => {
          const newRoutes = { ...state.routes };
          delete newRoutes[dayKey];
          return { routes: newRoutes };
        });
      },
      
      // Clear all routes
      clearAllRoutes: () => set({ routes: {} }),
    }),
    {
      name: 'route-storage',
    }
  )
);

/**
 * Messages Store
 * Stores all conversations and messages indexed by customer phone number
 * Structure:
 * {
 *   conversations: {
 *     "2391234567": {
 *       messages: [
 *         { id: "msg1", text: "Hello", direction: "inbound", timestamp: ... },
 *         ...
 *       ],
 *       metadata: {
 *         customerName: "John Doe",
 *         lastMessage: "Hello",
 *         ...
 *       }
 *     }
 *   }
 * }
 */
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const useMessagesStore = create((set, get) => ({
  conversations: {},
  initialized: false, // Track if messages have been loaded from DB
  
  // Mark store as initialized
  setInitialized: (value = true) => set({ initialized: value }),
  
  // Check if store is initialized
  isInitialized: () => get().initialized,
  
  // Initialize or bulk update conversations
  setConversations: (conversations) => set({ conversations, initialized: true }),
  
  // Update a single conversation's messages or metadata
  setConversation: (phone, data) => set((state) => ({
    conversations: {
      ...state.conversations,
      [phone]: {
        ...(state.conversations[phone] || {}), // Merge with existing data
        ...data
      }
    },
    initialized: true // Mark as initialized when any conversation is set
  })),

  // Get messages for a specific phone number
  getMessages: (phone) => {
    const state = get();
    return state.conversations[phone]?.messages || EMPTY_ARRAY;
  },

  // Get metadata for a specific phone number
  getMetadata: (phone) => {
    const state = get();
    return state.conversations[phone]?.metadata || EMPTY_OBJECT;
  }
}));

/**
 * Check-In Store
 * Manages check-in page data
 */
export const useCheckInStore = create(
  persist(
    (set, get) => ({
      // State
      booking: null,
      bookingIntake: {
        product1: {},
        product2: {},
      },
      products1Catalog: [],
      products2Catalog: [],
      shopInfo: {},
      storeLogo: "",
      product1CategoryName: "Product1",
      product2CategoryName: "Product2",

      // Actions
      setBooking: (booking) => set({ booking }),
      setBookingIntake: (bookingIntake) => set({ bookingIntake }),
      updateBookingIntake: (updater) => {
        const current = get().bookingIntake;
        const updated = typeof updater === 'function' ? updater(current) : updater;
        set({ bookingIntake: updated });
      },
      setProducts1Catalog: (products) => set({ products1Catalog: products }),
      setProducts2Catalog: (products) => set({ products2Catalog: products }),
      setShopInfo: (shopInfo) => set({ shopInfo }),
      setStoreLogo: (logo) => set({ storeLogo: logo }),
      setProduct1CategoryName: (name) => set({ product1CategoryName: name }),
      setProduct2CategoryName: (name) => set({ product2CategoryName: name }),
      reset: () => set({
        booking: null,
        bookingIntake: {
          product1: {},
          product2: {},
        },
        products1Catalog: [],
        products2Catalog: [],
        shopInfo: {},
        storeLogo: "",
        product1CategoryName: "Product1",
        product2CategoryName: "Product2",
      }),
    }),
    {
      name: 'checkin-storage',
      partialize: (state) => ({
        booking: state.booking,
        bookingIntake: state.bookingIntake,
        products1Catalog: state.products1Catalog,
        products2Catalog: state.products2Catalog,
        shopInfo: state.shopInfo,
        storeLogo: state.storeLogo,
        product1CategoryName: state.product1CategoryName,
        product2CategoryName: state.product2CategoryName,
      }),
    }
  )
);

/**
 * Users Store
 * Manages users data from tenant/store/users collection
 */
export const useUsersStore = create((set, get) => ({
  users: {}, // { [userId]: { id, firstName, lastName, authLevel, notificationsEnabled, ... } }
  currentUser: null, // Current logged-in user object from users collection
  
  setUsers: (users) => {
    set({ users });
    // Update currentUser if user is logged in
    // Use setTimeout to avoid circular dependency during initialization
    setTimeout(() => {
      try {
        const { useAuthStore } = require('./stores');
        const authStore = useAuthStore.getState();
        // Use selectedUser.id instead of user.uid, since users are keyed by their user ID
        if (authStore.selectedUser && authStore.selectedUser.id) {
          const currentUser = users[authStore.selectedUser.id] || null;
          set({ currentUser });
        }
      } catch (error) {
        // Ignore circular dependency errors during initialization
        console.warn('Could not update currentUser:', error);
      }
    }, 0);
  },
  
  setCurrentUser: (user) => set({ currentUser: user }),
  
  updateUser: (userId, updatedData) =>
    set((state) => ({
      users: {
        ...state.users,
        [userId]: { ...state.users[userId], ...updatedData },
      },
      // Update currentUser if this is the logged-in user
      currentUser: state.currentUser?.id === userId 
        ? { ...state.currentUser, ...updatedData }
        : state.currentUser,
    })),
  
  clearUsers: () => set({ users: {}, currentUser: null }),
}));

/**
 * Auth Store
 * Manages authentication state
 */
export const useAuthStore = create((set) => {
  // Load selectedUser from localStorage on initialization
  const loadSelectedUser = () => {
    try {
      const stored = localStorage.getItem('selectedUser');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading selectedUser from localStorage:', error);
      return null;
    }
  };

  return {
    user: null,
    isAuthenticated: false,
    isAdmin: false,
    // Drives SwitchUserOverlay. Distinct from logout(): the Firebase session is
    // kept, only the staff identity is dropped, so switching users never sends
    // anyone back to the email/password screen.
    switchingUser: false,
    // Why the pad is up: "login" right after the shared email sign-in, "switch"
    // from the header menu. Only the pad's title differs ("Sign in User" vs
    // "Switch User") — a freshly signed-in user has nobody to switch from.
    switchUserMode: null,
    authChecked: false,
    selectedUser: loadSelectedUser(), // Load from localStorage on init
    
    setUser: (user) => {
      set({ user, isAuthenticated: !!user });
      // Note: currentUser is updated via setUsers when users are loaded
      // This is handled by the setUsers function in useUsersStore
    },
    setSelectedUser: (selectedUser) => {
      set({ selectedUser });
      // Save to localStorage
      try {
        if (selectedUser) {
          localStorage.setItem('selectedUser', JSON.stringify(selectedUser));
        } else {
          localStorage.removeItem('selectedUser');
        }
      } catch (error) {
        console.error('Error saving selectedUser to localStorage:', error);
      }
      // Update currentUser in users store when selectedUser changes
      if (selectedUser && selectedUser.id) {
        setTimeout(() => {
          const usersStore = useUsersStore.getState();
          const currentUser = usersStore.users[selectedUser.id] || null;
          useUsersStore.getState().setCurrentUser(currentUser);
        }, 0);
      } else {
        // Clear currentUser if no selectedUser
        useUsersStore.getState().setCurrentUser(null);
      }
    },
    setIsAdmin: (isAdmin) => set({ isAdmin }),
    setAuthChecked: (authChecked) => set({ authChecked }),
    startSwitchUser: (mode = "switch") => {
      try {
        localStorage.removeItem('selectedUser');
      } catch (error) {
        console.error('Error clearing selectedUser from localStorage:', error);
      }
      useUsersStore.getState().setCurrentUser(null);
      set({ selectedUser: null, switchingUser: true, switchUserMode: mode });
    },
    endSwitchUser: () => set({ switchingUser: false, switchUserMode: null }),
    logout: () => {
      // Clear selectedUser from localStorage
      try {
        localStorage.removeItem('selectedUser');
      } catch (error) {
        console.error('Error clearing selectedUser from localStorage:', error);
      }
      set({ user: null, isAuthenticated: false, isAdmin: false, selectedUser: null, switchingUser: false, switchUserMode: null });
    },
  };
});

/**
 * Doc id of the PIN-selected staff member -- who is actually at the keyboard.
 * The Firebase login is shared by the whole store, so request.auth.uid can't
 * name a person. Outgoing texts carry this id so a customer's reply can be
 * forwarded to that person's cell. Null before anyone has picked a user.
 */
export const getSelectedUserId = () =>
  useAuthStore.getState().selectedUser?.id || null;

/**
 * Road-Call Store
 * Live call-expectation docs from the call-expectations collection, fed by
 * listenToCallExpectations. The CallbackBanner filters this to the current
 * user's own expectation and skips expired ones client-side; the
 * roadVoiceInbound webhook independently checks expiresAt server-side.
 */
export const useRoadCallStore = create((set) => ({
  callExpectations: [],
  setCallExpectations: (callExpectations) => set({ callExpectations: callExpectations || [] }),
}));

/**
 * New Booking Store
 * Manages new booking form data (persisted to localStorage)
 */
const touchNewBooking = () => ({ lastUpdatedAt: Date.now() });

export const useNewBookingStore = create(
  persist(
    (set) => ({
      // Timestamp of last change (for clearing stale data after 120s)
      lastUpdatedAt: null,

      // Dates (stored as ISO strings, converted to Date objects when accessed)
      startDate: null,
      endDate: null,
      currentMonth: new Date().toISOString(),
      
      // Selected items
      selectedBikes: {},
      selectedAddOns: {},

      // Admin-only negotiated per-unit daily rate, in CENTS. null = use catalog prices.
      dailyRateOverrideCents: null,

      // Delivery options
      deliveryNeeded: false,
      homePickupNeeded: false,
      inStorePickup: false,
      gatedCommunity: false,
      communityName: "",
      addressNotes: "",
      
      // Customer info
      firstName: "",
      lastName: "",
      customerEmail: "",
      customerPhone: "",
      
      // Address
      addressInput: "",
      // True only once the user taps a suggestion from the address dropdown —
      // distinct from addressConfirmed, which tracks the distance lookup.
      addressSelected: false,
      addressConfirmed: false,
      
      // Waiver
      waiverSigned: false,
      waiverData: null,
      
      // Actions (each update touches lastUpdatedAt)
      setStartDate: (date) => set({ startDate: date ? (date instanceof Date ? date.toISOString() : date) : null, ...touchNewBooking() }),
      setEndDate: (date) => set({ endDate: date ? (date instanceof Date ? date.toISOString() : date) : null, ...touchNewBooking() }),
      setCurrentMonth: (month) => set({ currentMonth: month instanceof Date ? month.toISOString() : month, ...touchNewBooking() }),
      setSelectedBikes: (bikes) => set({ selectedBikes: bikes, ...touchNewBooking() }),
      setSelectedAddOns: (addOns) => set({ selectedAddOns: addOns, ...touchNewBooking() }),
      setDeliveryNeeded: (needed) => set({ deliveryNeeded: needed, ...touchNewBooking() }),
      setHomePickupNeeded: (needed) => set({ homePickupNeeded: needed, ...touchNewBooking() }),
      setInStorePickup: (pickup) => set({ inStorePickup: pickup, ...touchNewBooking() }),
      setGatedCommunity: (gated) => set({ gatedCommunity: gated, ...touchNewBooking() }),
      setCommunityName: (name) => set({ communityName: name, ...touchNewBooking() }),
      setAddressNotes: (notes) => set({ addressNotes: notes, ...touchNewBooking() }),
      setFirstName: (name) => set({ firstName: name, ...touchNewBooking() }),
      setLastName: (name) => set({ lastName: name, ...touchNewBooking() }),
      setCustomerEmail: (email) => set({ customerEmail: email, ...touchNewBooking() }),
      setCustomerPhone: (phone) => set({ customerPhone: phone, ...touchNewBooking() }),
      setAddressInput: (address) => set({ addressInput: address, ...touchNewBooking() }),
      setAddressConfirmed: (confirmed) => set({ addressConfirmed: confirmed, ...touchNewBooking() }),
      setWaiverSigned: (signed) => set({ waiverSigned: signed, ...touchNewBooking() }),
      setWaiverData: (data) => set({ waiverData: data, ...touchNewBooking() }),
      
      // Update multiple fields at once
      updateBookingData: (data) => set((state) => ({ ...state, ...data, ...touchNewBooking() })),
      
      // Clear all booking data
      clearBooking: () => set({
        lastUpdatedAt: null,
        startDate: null,
        endDate: null,
        currentMonth: new Date(),
        selectedBikes: {},
        selectedAddOns: {},
        dailyRateOverrideCents: null,
        deliveryNeeded: false,
        homePickupNeeded: false,
        inStorePickup: false,
        gatedCommunity: false,
        communityName: "",
        addressNotes: "",
        firstName: "",
        lastName: "",
        customerEmail: "",
        customerPhone: "",
        addressInput: "",
        addressSelected: false,
        addressConfirmed: false,
        waiverSigned: false,
        waiverData: null,
      }),
    }),
    {
      name: 'new-booking-storage', // localStorage key
    }
  )
);
